import { create } from "zustand";

/**
 * What the engineer chose per subject while triaging an item: swap a device's battery, clean up a policy,
 * or ignore it. The queue API has nowhere to send these yet (a decision carries only a reason), so this
 * store only remembers them for the screen. When the backend can take them, `selections[itemId]` is the
 * object to send, and nothing else in the UI needs to change.
 */
export type TriageChoice = "swap" | "cleanup" | "ignore";

export type TriageSelection = Record<string, TriageChoice>;

interface TriageState {
    /** Item id, then the subject's key (a device id or a policy key), then the choice. */
    selections: Record<string, TriageSelection>;
    /** Sets the choice; choosing the one already set clears it, so a second click undoes it. */
    toggle: (itemId: string, key: string, choice: TriageChoice) => void;
    /** Sets the same choice on many subjects, or clears them when `choice` is null. */
    setMany: (
        itemId: string,
        keys: string[],
        choice: TriageChoice | null
    ) => void;
}

const useAidwopsTriageStore = create<TriageState>((set) => ({
    selections: {},
    toggle: (itemId, key, choice) =>
        set((state) => {
            const current = { ...(state.selections[itemId] ?? {}) };
            if (current[key] === choice) {
                delete current[key];
            } else {
                current[key] = choice;
            }
            return { selections: { ...state.selections, [itemId]: current } };
        }),
    setMany: (itemId, keys, choice) =>
        set((state) => {
            const current = { ...(state.selections[itemId] ?? {}) };
            for (const key of keys) {
                if (choice === null) {
                    delete current[key];
                } else {
                    current[key] = choice;
                }
            }
            return { selections: { ...state.selections, [itemId]: current } };
        }),
}));

/** Stable empty value, so a selector for an item with no choices yet does not return a new object on every render. */
export const NO_SELECTION: TriageSelection = {};

export default useAidwopsTriageStore;
